import type { Language } from '@nxt-insurance/constants'
import { SupportedMarketKey } from '@nxt-insurance/constants'

import { getFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import {
  appointmentDetailsSchema,
  appointment_details,
} from '@flows/configs/shared/mgaLight/steps/appointmentDetails'
import { getLoginSchema, login } from '@flows/configs/shared/mgaLight/steps/login'
import { phoneNumberConfirmation } from '@flows/configs/shared/mgaLight/steps/phoneConfirmation'
import { progressFirstStep } from '@flows/configs/shared/mgaLight/steps/progressFirstStep'
import type { AnySteps } from '@flows/core/types/stepTypes'
import { generateGetFlowValueFunctions } from '@flows/insurance/utils'

import { earnsAboveThreshold } from './steps/earnsAboveThreshold'
import { hasExistingDentalInsurance } from './steps/hasExistingDentalInsurance'
import { healthInsuranceType } from './steps/healthInsuranceType'
import { ineligibleStatus } from './steps/ineligibleStatus'
import { progressLastStep } from './steps/progressLastStep'
import { redirectToAllianz } from './steps/redirectToAllianz'
import { redirectToAllianzWeb } from './steps/redirectToAllianzWeb'
import { success } from './steps/success'
import { dentalInsuranceSchema } from './types'

const helperFunctions = generateGetFlowValueFunctions('mga_dental_de')

const schema = dentalInsuranceSchema.merge(getLoginSchema()).merge(appointmentDetailsSchema)

export const getDentalInsuranceConfig = (args: {
  getCurrentLanguage: () => Language
  platform: 'web' | 'native'
  environment: string
}) => {
  const params = {
    helperFunctions,
    schema,
    getCurrentLanguage: args.getCurrentLanguage,
    platform: args.platform,
    environment: args.environment,
  }
  const steps = {
    // TODO: This step is temporarily not used. But we don't plan to completely remove it, therefore the code should remain here
    // This will be re-checked in the scope of this ticket: https://hellogetsafe.atlassian.net/browse/IA-540
    progress_first_step: progressFirstStep({
      platform: args.platform,
      nextStep: 'health_insurance_type',
    }),
    health_insurance_type: healthInsuranceType(params),
    has_existing_dental_insurance: hasExistingDentalInsurance(params),
    earns_above_threshold: earnsAboveThreshold({ ...params, platform: args.platform }),
    ineligible_status: ineligibleStatus(params),
    redirect_to_allianz: redirectToAllianz(),
    // TODO: https://hellogetsafe.atlassian.net/browse/IA-1318
    // Since the web dose not accommodate the implementation of the customButton, we created a separate step for the web.
    // This is a temporary solution for now.
    redirect_to_allianz_web: redirectToAllianzWeb(),
    login: login({ market: SupportedMarketKey.De, platform: args.platform }),
    phone_confirmation: phoneNumberConfirmation({ market: SupportedMarketKey.De }),
    progress_last_step: progressLastStep(params),
    success: success(params),
    appointment_details: appointment_details({
      type: 'translatableText',
      translationKey: 'sharedFlows_dental:dental_insurance.title',
    }),
  } as const satisfies AnySteps

  return getFlowConfiguration(steps, {
    constants: {
      flowName: 'dental-insurance',
      SCHEMA: schema,
      FIRST_STEP_NAME: 'health_insurance_type',
      productKey: 'mga_dental_de',
    },
  })
}
